import { FeatureFlagNames } from "./enums";
import { InputCategories } from "../globals";

export type Modifier = number | number[];

export interface IKeybinding {
	keycode: number;
	modifiers: Array<Modifier>;
	name?: string;
	featureFlag?: FeatureFlagNames;
	category?: InputCategories;
	nameGetter?: KeybindingNameGetters;
}

export enum MouseButtons {
	LEFT = 0,
	MIDDLE = 1,
	RIGHT = 2,
}

export const KeyCodes: Record<string | number, number> = {
	BACKSPACE: 8,
	TAB: 9,
	ENTER: 13,
	PAUSE: 19,
	CAPS_LOCK: 20,
	ESCAPE: 27,
	SPACE: 32,
	PAGE_UP: 33,
	PAGE_DOWN: 34,
	END: 35,
	HOME: 36,
	LEFT_ARROW: 37,
	UP_ARROW: 38,
	RIGHT_ARROW: 39,
	DOWN_ARROW: 40,
	INSERT: 45,
	DELETE: 46,
	0: 48,
	1: 49,
	2: 50,
	3: 51,
	4: 52,
	5: 53,
	6: 54,
	7: 55,
	8: 56,
	9: 57,
	n0: 48,
	n1: 49,
	n2: 50,
	n3: 51,
	n4: 52,
	n5: 53,
	n6: 54,
	n7: 55,
	n8: 56,
	n9: 57,
	A: 65,
	B: 66,
	C: 67,
	D: 68,
	E: 69,
	F: 70,
	G: 71,
	H: 72,
	I: 73,
	J: 74,
	K: 75,
	L: 76,
	M: 77,
	N: 78,
	O: 79,
	P: 80,
	Q: 81,
	R: 82,
	S: 83,
	T: 84,
	U: 85,
	V: 86,
	W: 87,
	X: 88,
	Y: 89,
	Z: 90,
	NUMPAD_0: 96,
	NUMPAD_1: 97,
	NUMPAD_2: 98,
	NUMPAD_3: 99,
	NUMPAD_4: 100,
	NUMPAD_5: 101,
	NUMPAD_6: 102,
	NUMPAD_7: 103,
	NUMPAD_8: 104,
	NUMPAD_9: 105,
	MULTIPLY: 106,
	ADD: 107,
	SUBTRACT: 109,
	DECIMAL: 110,
	DIVIDE: 111,
	F1: 112,
	F2: 113,
	F3: 114,
	F4: 115,
	F5: 116,
	F6: 117,
	F7: 118,
	F8: 119,
	F9: 120,
	F10: 121,
	F11: 122,
	F12: 123,
	NUM_LOCK: 144,
	SCROLL_LOCK: 145,
	MINUS_FIREFOX: 173,
	SEMICOLON: 186,
	EQUALS: 187,
	COMMA: 188,
	DASH: 189,
	PERIOD: 190,
	FORWARD_SLASH: 191,
	GRAVE_ACCENT: 192,
	OPEN_BRACKET: 219,
	BACK_SLASH: 220,
	CLOSE_BRACKET: 221,
	SINGLE_QUOTE: 222
};

export const ModifierKeyCodes: Record<string, number | number[]> = {
	SHIFT: 16,
	CTRL: 17,
	ALT: 18,
	COMMAND: [91, 93, 224],
};

export enum KeybindingNameGetters {
	FactoryPresets = "FactoryPresets",
	UserDefinedLayouts = "UserDefinedLayouts",
}

/**
 * the factory default keybindings object
 * dev note: modifier arrays with inner arrays (for CTRL, COMMAND usually) must be explicitly defined as number[][]
 *
 * @static
 * @memberof Input
 */
export const FactoryDefaultKeyBindings: Record<string, IKeybinding> = {
	//Gizmo
	GizmoModeTranslate:
	{
		category: InputCategories.TransformGizmo,
		name: "Translate",
		keycode: KeyCodes.W,
		modifiers: []
	},
	GizmoModeRotate:
	{
		category: InputCategories.TransformGizmo,
		name: "Rotate",
		keycode: KeyCodes.E,
		modifiers: []
	},
	GizmoModeScale:
	{
		category: InputCategories.TransformGizmo,
		name: "Scale",
		keycode: KeyCodes.R,
		modifiers: []
	},
	GizmoHide:
	{
		category: InputCategories.TransformGizmo,
		name: "Visibility Toggle",
		keycode: KeyCodes.Q,
		modifiers: []
	},
	GizmoModeSpaceToggle:
	{
		category: InputCategories.TransformGizmo,
		name: "Space Toggle",
		keycode: KeyCodes.X,
		modifiers: []
	},
	GizmoModePivotToggle:
	{
		category: InputCategories.TransformGizmo,
		name: "Pivot Toggle",
		keycode: KeyCodes.Z,
		modifiers: []
	},
	
	//CameraUtilities
	CameraFocus:
	{
		category: InputCategories.CameraUtilities,
		name: "Focus Selected",
		keycode: KeyCodes.F,
		modifiers: []
	},
	AlignToView:
	{
		category: InputCategories.CameraUtilities,
		name: "Align To View",
		keycode: KeyCodes.F,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.SHIFT] as number[][],
	},
	CameraRestoreOrientation:
	{
		category: InputCategories.CameraUtilities,
		name: "Restore Default Orientation",
		keycode: KeyCodes.HOME,
		modifiers: []
	},
	CameraProjectionMode:
	{
		category: InputCategories.CameraUtilities,
		name: "Toggle Camera Projection Mode",
		keycode: KeyCodes.O,
		modifiers: []
	},
	//Camera Movement
	CameraForward:
	{
		category: InputCategories.CameraMovement,
		name: "Move Forwards",
		keycode: KeyCodes.W,
		modifiers: []
	},
	CameraBackwards:
	{
		category: InputCategories.CameraMovement,
		name: "Move Backwards",
		keycode: KeyCodes.S,
		modifiers: []
	},
	CameraLeft:
	{
		category: InputCategories.CameraMovement,
		name: "Move Left",
		keycode: KeyCodes.A,
		modifiers: []
	},
	CameraRight:
	{
		category: InputCategories.CameraMovement,
		name: "Move Right",
		keycode: KeyCodes.D,
		modifiers: []
	},
	CameraUp:
	{
		category: InputCategories.CameraMovement,
		name: "Move Upwards",
		keycode: KeyCodes.E,
		modifiers: []
	},
	CameraDown:
	{
		category: InputCategories.CameraMovement,
		name: "Move Downwards",
		keycode: KeyCodes.Q,
		modifiers: []
	},

	//Reserved keys
	Open:
	{
		category: InputCategories.Reserved,
		name: "Open Scenario",
		keycode: KeyCodes.O,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	Save:
	{
		category: InputCategories.Reserved,
		name: "Save Scenario",
		keycode: KeyCodes.S,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	SaveAs:
	{
		category: InputCategories.Reserved,
		name: "Save Scenario As",
		keycode: KeyCodes.S,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.SHIFT] as number[][],
	},
	Share:
	{
		category: InputCategories.Reserved,
		name: "Share",
		keycode: KeyCodes.S,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT] as number[][],
	},
	NewRevision:
	{
		category: InputCategories.Reserved,
		name: "New Revision",
		keycode: KeyCodes.R,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT] as number[][],
	},
	Publish:
	{
		category: InputCategories.Reserved,
		name: "Publish",
		keycode: KeyCodes.P,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	DownloadScenario:
	{
		category: InputCategories.Reserved,
		name: "Download Scenario",
		keycode: KeyCodes.P,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.SHIFT] as number[][],
	},
	Copy:
	{
		category: InputCategories.Reserved,
		name: "Copy",
		keycode: KeyCodes.C,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	Cut:
	{
		category: InputCategories.Reserved,
		name: "Cut",
		keycode: KeyCodes.X,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	Paste:
	{
		category: InputCategories.Reserved,
		name: "Paste",
		keycode: KeyCodes.V,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	Duplicate:
	{
		category: InputCategories.Reserved,
		name: "Duplicate",
		keycode: KeyCodes.D,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	SelectAll:
	{
		category: InputCategories.Reserved,
		name: "SelectAll",
		keycode: KeyCodes.A,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	DeselectAll:
	{
		category: InputCategories.Reserved,
		name: "Deselect All",
		keycode: KeyCodes.A,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.SHIFT] as number[][],
	},
	Delete:
	{
		category: InputCategories.Reserved,
		name: "Delete",
		keycode: KeyCodes.DELETE,
		modifiers: []
	},
	Undo:
	{
		category: InputCategories.Reserved,
		name: "Undo",
		keycode: KeyCodes.Z,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	Redo:
	{
		category: InputCategories.Reserved,
		name: "Redo",
		keycode: KeyCodes.Z,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.SHIFT] as number[][],
	},
	Redo2:
	{
		category: InputCategories.Reserved,
		name: "Redo",
		keycode: KeyCodes.Y,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	DeleteProject:
	{
		category: InputCategories.Reserved,
		name: "Delete Project",
		keycode: KeyCodes.DELETE,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.SHIFT, ModifierKeyCodes.ALT] as number[][],
	},
	
	// windows
	AddSceneView:
	{
		category: InputCategories.Window,
		name: "Add Scene View",
		keycode: KeyCodes.V,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT] as number[][],
	},
	AssetLibrary:
	{
		category: InputCategories.Window,
		name: "Asset Library",
		keycode: KeyCodes.L,
		modifiers: [ModifierKeyCodes.ALT]
	},
	ObjectGroups:
	{
		category: InputCategories.Window,
		name: "Object Groups",
		keycode: KeyCodes.G,
		modifiers: [ModifierKeyCodes.ALT]
	},
	RevisionsEditor:
	{
		category: InputCategories.Window,
		name: "Revisions Editor",
		keycode: KeyCodes.R,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT] as number[][],
	},
	StepHierarchy:
	{
		category: InputCategories.Window,
		name: "Step Hierarchy",
		keycode: KeyCodes.H,
		modifiers: [ModifierKeyCodes.ALT]
	},
	ChecklistEditor:
	{
		category: InputCategories.Window,
		name: "Form Editor",
		keycode: KeyCodes.F,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT] as number[][],
	},
	ChatGPTEditor:
	{
		category: InputCategories.Window,
		name: "ChatGPT",
		keycode: KeyCodes.C,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT] as number[][],
	},
	SequenceEditor:
	{
		category: InputCategories.Window,
		name: "Sequence Editor",
		keycode: KeyCodes.S,
		modifiers: [ModifierKeyCodes.ALT]
	},
	TimelineEditor:
	{
		category: InputCategories.Window,
		name: "Timeline Editor",
		keycode: KeyCodes.T,
		modifiers: [ModifierKeyCodes.ALT]
	},
	UIEditor:
	{
		category: InputCategories.Window,
		name: "UI Editor (Legacy)",
		keycode: KeyCodes.U,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT] as number[][]
	},
	VisageEditor:
	{
		category: InputCategories.Window,
		name: "UI Editor",
		keycode: KeyCodes.U,
		modifiers: [ModifierKeyCodes.ALT],
	},
	ObjectProperties:
	{
		category: InputCategories.Window,
		name: "Object Properties",
		keycode: KeyCodes.O,
		modifiers: [ModifierKeyCodes.ALT]
	},
	ARTrackers:
	{
		category: InputCategories.Window,
		name: "AR Trackers",
		keycode: KeyCodes.A,
		modifiers: [ModifierKeyCodes.ALT]
	},
	ActionHistory:
	{
		category: InputCategories.Window,
		name: "History",
		keycode: KeyCodes.H,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT] as number[][],
	},
	ProjectHealth:
	{
		category: InputCategories.Window,
		name: "Project Health",
		keycode: KeyCodes.H,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT] as number[][],
	},
	IoT:
	{
		category: InputCategories.Window,
		name: "IoT",
		keycode: KeyCodes.I,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT] as number[][],
		featureFlag: FeatureFlagNames.connectedServices,
	},
	Attachments:
	{
		category: InputCategories.Window,
		name: "Attachments",
		keycode: KeyCodes.K,
		modifiers: [ModifierKeyCodes.CTRL, ModifierKeyCodes.SHIFT]
	},
	StepConditions:
	{
		category: InputCategories.Window,
		name: "Step Conditions",
		keycode: KeyCodes.C,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT] as number[][],
		featureFlag: FeatureFlagNames.connectedServices,
	},
	StepActions:
	{
		category: InputCategories.Window,
		name: "Step Actions",
		keycode: KeyCodes.A,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.SHIFT] as number[][],
	},
	PartsCatalogEditor:
	{
		category: InputCategories.Window,
		name: "Parts Catalog Editor",
		keycode: KeyCodes.P,
		modifiers: [ModifierKeyCodes.ALT],
		featureFlag: FeatureFlagNames.partsCatalog,
	},
	ShowHelpWindow:
	{
		category: InputCategories.Window,
		name: "Show Help",
		keycode: KeyCodes.H,
		modifiers: [ModifierKeyCodes.ALT]
	},
	CloseAll:
	{
		category: InputCategories.Window,
		name: "Close All",
		keycode: KeyCodes.SPACE,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},

	// workspace
	SaveCustomWorkspace:
	{
		category: InputCategories.Workspace,
		name: "Save Custom Workspace",
		keycode: KeyCodes.S,
		modifiers: [ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT]
	},
	FactoryPreset0:
	{
		category: InputCategories.Workspace,
		name: "0", //(DEFAULT WORKSPACE_//TODO: dynamically pull the name from the list of factory presets
		nameGetter: KeybindingNameGetters.FactoryPresets,
		keycode: KeyCodes.n0,
		modifiers: [ModifierKeyCodes.ALT]
	},
	FactoryPreset1:
	{
		category: InputCategories.Workspace,
		name: "1", //TODO: dynamically pull the name from the list of factory presets
		nameGetter: KeybindingNameGetters.FactoryPresets,
		keycode: KeyCodes.n1,
		modifiers: [ModifierKeyCodes.ALT]
	},
	FactoryPreset2:
	{
		category: InputCategories.Workspace,
		name: "2", //TODO: dynamically pull the name from the list of factory presets
		nameGetter: KeybindingNameGetters.FactoryPresets,
		keycode: KeyCodes.n2,
		modifiers: [ModifierKeyCodes.ALT]
	},
	FactoryPreset3:
	{
		category: InputCategories.Workspace,
		name: "3", //TODO: dynamically pull the name from the list of factory presets
		nameGetter: KeybindingNameGetters.FactoryPresets,
		keycode: KeyCodes.n3,
		modifiers: [ModifierKeyCodes.ALT]
	},
	UserDefinedLayout0:
	{
		category: InputCategories.Workspace,
		name: "0", //TODO: dynamically pull the name from the list of factory presets
		nameGetter: KeybindingNameGetters.UserDefinedLayouts,
		keycode: KeyCodes.n5,
		modifiers: [ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT]
	},
	UserDefinedLayout1:
	{
		category: InputCategories.Workspace,
		name: "1",
		nameGetter: KeybindingNameGetters.UserDefinedLayouts,
		keycode: KeyCodes.n6,
		modifiers: [ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT]
	},
	UserDefinedLayout2:
	{
		category: InputCategories.Workspace,
		name: "2",
		nameGetter: KeybindingNameGetters.UserDefinedLayouts,
		keycode: KeyCodes.n7,
		modifiers: [ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT]
	},
	UserDefinedLayout3:
	{
		category: InputCategories.Workspace,
		name: "3",
		nameGetter: KeybindingNameGetters.UserDefinedLayouts,
		keycode: KeyCodes.n8,
		modifiers: [ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT]
	},
	UserDefinedLayout4:
	{
		category: InputCategories.Workspace,
		name: "4",
		nameGetter: KeybindingNameGetters.UserDefinedLayouts,
		keycode: KeyCodes.n9,
		modifiers: [ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT]
	},

	// scenario
	AddNewStep:
	{
		category: InputCategories.Scenario,
		name: "Create New Step",
		keycode: KeyCodes.N,
		modifiers: [ModifierKeyCodes.ALT]
	},
	AddNewSequence:
	{
		category: InputCategories.Scenario,
		name: "Create New Sequence",
		keycode: KeyCodes.N,
		modifiers: [ModifierKeyCodes.ALT, ModifierKeyCodes.SHIFT]
	},
	GoToNextSequence:
	{
		category: InputCategories.Scenario,
		name: "Navigate to Next Sequence",
		keycode: KeyCodes.PAGE_DOWN,
		modifiers: [ModifierKeyCodes.ALT]
	},
	GoToPreviousSequence:
	{
		category: InputCategories.Scenario,
		name: "Navigate to Previous Sequence",
		keycode: KeyCodes.PAGE_UP,
		modifiers: [ModifierKeyCodes.ALT]
	},
	GoToNextStep:
	{
		category: InputCategories.Scenario,
		name: "Navigate to Next Step",
		keycode: KeyCodes.PAGE_DOWN,
		modifiers: []
	},
	GoToPreviousStep:
	{
		category: InputCategories.Scenario,
		name: "Navigate to Previous Step",
		keycode: KeyCodes.PAGE_UP,
		modifiers: []
	},
	DeleteConnection:
	{
		category: InputCategories.Scenario,
		name: "Delete the selected connection",
		keycode: KeyCodes.DELETE,
		modifiers: []
	},

	// general
	ToggleSimulation:
	{
		category: InputCategories.General,
		name: "Start/Stop Simulation",
		keycode: KeyCodes.SPACE,
		modifiers: []
	},
	CurrentStepSimulation:
	{
		category: InputCategories.General,
		name: "Simulate Current Step",
		keycode: KeyCodes.SPACE,
		modifiers: [ModifierKeyCodes.SHIFT]
	},
	SimulateAsViewer:
	{
		category: InputCategories.General,
		name: "Simulate as Viewer",
		keycode: KeyCodes.SPACE,
		modifiers: [ModifierKeyCodes.CTRL, ModifierKeyCodes.SHIFT]
	},
	UIToggleVisibility:
	{
		category: InputCategories.General,
		name: "Toggle UI Visibility",
		keycode: KeyCodes.U,
		modifiers: []
	},

	// timeline
	TogglePlayTimeline:
	{
		category: InputCategories.Timeline,
		name: "Play/Pause Timeline",
		keycode: KeyCodes.T,
		modifiers: [ModifierKeyCodes.SHIFT]
	},
	ToggleRecordTimeline:
	{
		category: InputCategories.Timeline,
		name: "Start/Stop Recording",
		keycode: KeyCodes.R,
		modifiers: [ModifierKeyCodes.ALT]
	},
	CreateTimelineRecord:
	{
		category: InputCategories.Timeline,
		name: "Create Timeline Record",
		keycode: KeyCodes.K,
		modifiers: []
	},
	SelectPreviousTimebars:
	{
		category: InputCategories.Timeline,
		name: "Select Previous Timebars",
		keycode: KeyCodes.COMMA,
		modifiers: [ModifierKeyCodes.SHIFT]
	},
	SelectNextTimebars:
	{
		category: InputCategories.Timeline,
		name: "Select Next Timebars",
		keycode: KeyCodes.PERIOD,
		modifiers: [ModifierKeyCodes.SHIFT]
	},

	// visibility
	IsolateVisibility:
	{
		category: InputCategories.ObjectVisibility,
		name: "Isolate Selected",
		keycode: KeyCodes.I,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	HideVisibilityNoneRecursive:
	{
		category: InputCategories.ObjectVisibility,
		name: "Hide Selected",
		keycode: KeyCodes.H,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.SHIFT] as number[][],
	},
	HideVisibility:
	{
		category: InputCategories.ObjectVisibility,
		name: "Hide Selected Tree",
		keycode: KeyCodes.H,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
	UnhideSelectedVisibility:
	{
		category: InputCategories.ObjectVisibility,
		name: "Unhide Selected",
		keycode: KeyCodes.U,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND], ModifierKeyCodes.SHIFT] as number[][],
	},
	UnhideVisibility:
	{
		category: InputCategories.ObjectVisibility,
		name: "Unhide All",
		keycode: KeyCodes.U,
		modifiers: [[ModifierKeyCodes.CTRL, ModifierKeyCodes.COMMAND]] as number[][],
	},
};